import { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";

// Final closing date for the Canadian Opinions poll.
// Anchored to midnight Eastern (America/Toronto) on Sep 15, 2029 — exactly one
// month before the next Canadian federal election (Oct 15, 2029).
// 04:00 UTC = 00:00 EDT.
const CLOSE_AT = new Date("2029-09-15T04:00:00Z").getTime();

function diff(now) {
  const ms = CLOSE_AT - now;
  if (ms <= 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
}

function Cell({ value, label, testid }) {
  return (
    <div className="bg-white border-2 border-black px-3 py-3 sm:px-4 sm:py-4 text-center min-w-[4.5rem] sm:min-w-[5.5rem]" data-testid={testid}>
      <div className="font-serif-display text-black leading-none text-3xl sm:text-4xl tabular-nums" style={{ fontWeight: 800 }}>
        {String(value).padStart(2, "0")}
      </div>
      <div className="mt-1 font-mono-civic text-[9px] sm:text-[10px] uppercase tracking-widest text-black/70" style={{ fontWeight: 800 }}>
        {label}
      </div>
    </div>
  );
}

export default function ClosingCountdown() {
  const { t } = useI18n();
  const [parts, setParts] = useState(() => diff(Date.now()));

  useEffect(() => {
    const id = setInterval(() => setParts(diff(Date.now())), 1000);
    return () => clearInterval(id);
  }, []);

  if (parts == null) {
    // Poll has closed
    return (
      <section className="mt-12 sm:mt-14 border-t-2 border-b-2 border-black bg-white py-8 sm:py-10 px-5 sm:px-8" data-testid="closing-countdown-closed">
        <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-[#E00000] flex items-center gap-2" style={{ fontWeight: 900 }}>
          <span className="w-2 h-2 bg-[#E00000]" />
          {t("countdown_tag")}
        </div>
        <h2 className="mt-2 font-serif-display text-black leading-[0.95] text-3xl sm:text-4xl" style={{ fontWeight: 800 }}>
          {t("countdown_closed_h")}
        </h2>
        <p className="mt-2 text-black max-w-2xl text-sm sm:text-base" style={{ fontWeight: 700 }}>
          {t("countdown_closed_p")}
        </p>
      </section>
    );
  }

  return (
    <section className="mt-12 sm:mt-14 border-t-2 border-b-2 border-black bg-[#F4F4F4] py-8 sm:py-10 px-5 sm:px-8" data-testid="closing-countdown">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-[#E00000] flex items-center gap-2" style={{ fontWeight: 900 }} data-testid="closing-countdown-tag">
            <span className="w-2 h-2 bg-[#E00000] animate-pulse" />
            {t("countdown_tag")}
          </div>
          <p className="mt-2 text-black max-w-2xl text-sm sm:text-base" style={{ fontWeight: 700 }}>
            {t("countdown_intro")}
          </p>
        </div>
        <div className="flex items-stretch gap-2 sm:gap-3 flex-wrap" data-testid="closing-countdown-clock">
          <Cell value={parts.days} label={t("countdown_days")} testid="countdown-days" />
          <Cell value={parts.hours} label={t("countdown_hours")} testid="countdown-hours" />
          <Cell value={parts.minutes} label={t("countdown_minutes")} testid="countdown-minutes" />
          <Cell value={parts.seconds} label={t("countdown_seconds")} testid="countdown-seconds" />
        </div>
      </div>
    </section>
  );
}
